import * as R from "ramda";

import { AvailableModel } from "../../../services/claude";
import { logError } from "../../../utils/error";
import { LENS_NAVIGATION_DIRECTION, LensNavigationDirection } from "./lensNavigation";

const LENS_HISTORY_KEY = "enlighten_lens_history";
const MAX_LENS_HISTORY = 10000;
const FIRST_ITEM_INDEX = 0;
const EMPTY_HISTORY_INDEX = -1;
const FIRST_VERSION = 1;
const RANDOM_BASE = 36;
const RANDOM_SUBSTR_START = 2;
const RANDOM_SUBSTR_END = 9;

const isValidIndex = (index: number, items: unknown[]): boolean =>
  index >= FIRST_ITEM_INDEX && index < items.length;

const lastIndexOf = <T>(items: T[]): number => R.dec(items.length);

const isFirstItemIndex = (index: number): boolean => index === FIRST_ITEM_INDEX;

export interface LensHistoryItem {
  id: string;
  name: string;
  content: string;
  model: AvailableModel;
  temperature: number;
  tokens: number;
  createdAt: string;
  lastResult?: unknown;
  isNamed: boolean;
  version: number;
  interactionId?: string;
  tenantId?: string;
  tenantName?: string;
}

export interface LensHistory {
  items: LensHistoryItem[];
  currentIndex: number;
}

export interface LensNavigationInfo {
  canGoBack: boolean;
  canGoForward: boolean;
  currentIndex: number;
  totalCount: number;
}

const generateLensId = (): string =>
  `lens_${Date.now()}_${Math.random().toString(RANDOM_BASE).substr(RANDOM_SUBSTR_START, RANDOM_SUBSTR_END)}`;

const isLensConfigurationDifferent = (
  current: Omit<LensHistoryItem, "id" | "createdAt" | "isNamed" | "version">,
  previous: Omit<LensHistoryItem, "id" | "createdAt" | "isNamed" | "version">
): boolean =>
  current.content !== previous.content ||
  current.model !== previous.model ||
  current.temperature !== previous.temperature ||
  current.tokens !== previous.tokens ||
  current.name !== previous.name ||
  current.interactionId !== previous.interactionId;

const getNextVersionNumber = (lensName: string): number => {
  const history = getLensHistory();

  const versionsForName = history.items
    .filter(item => item.name === lensName)
    .map(item => item.version || FIRST_VERSION);

  if (R.isEmpty(versionsForName)) {
    return FIRST_VERSION;
  }

  return R.inc(Math.max(...versionsForName));
};

export const getLensHistory = (): LensHistory => {
  try {
    const savedValue = localStorage.getItem(LENS_HISTORY_KEY);
    if (savedValue === null) {
      return { items: [], currentIndex: EMPTY_HISTORY_INDEX };
    }

    const parsed = JSON.parse(savedValue) as LensHistory;

    if (!Array.isArray(parsed.items)) {
      return { items: [], currentIndex: EMPTY_HISTORY_INDEX };
    }

    return parsed;
  } catch (error) {
    logError("Failed to retrieve lens history from localStorage", error);
    return { items: [], currentIndex: EMPTY_HISTORY_INDEX };
  }
};

const saveLensHistory = (history: LensHistory): void => {
  try {
    localStorage.setItem(LENS_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    logError("Failed to save lens history to localStorage", error);
  }
};

type AddLensToHistoryParams = {
  content: string;
  model: AvailableModel;
  temperature: number;
  tokens: number;
  lastResult: unknown;
  lensName?: string;
  interactionId?: string;
  tenantId?: string;
  tenantName?: string;
};

const isCurrentConfigUnchanged = (
  history: LensHistory,
  newConfig: Omit<LensHistoryItem, "id" | "createdAt" | "isNamed" | "version">
): boolean => {
  if (!isValidIndex(history.currentIndex, history.items)) {
    return false;
  }

  const currentItem = history.items[history.currentIndex];
  const currentConfig = {
    name: currentItem.name,
    interactionId: currentItem.interactionId,
    content: currentItem.content,
    model: currentItem.model,
    temperature: currentItem.temperature,
    tokens: currentItem.tokens
  };

  return !isLensConfigurationDifferent(newConfig, currentConfig);
};

const trimHistory = (items: LensHistoryItem[]): LensHistoryItem[] => {
  if (items.length <= MAX_LENS_HISTORY) {
    return items;
  }
  return items.slice(items.length - MAX_LENS_HISTORY);
};

export const addLensToHistory = (
  params: AddLensToHistoryParams
): { added: boolean; currentIndex: number } => {
  const { content, model, temperature, tokens, lastResult, lensName, interactionId, tenantId, tenantName } = params;
  const history = getLensHistory();
  const finalLensName = lensName ?? "";

  const newConfig = { name: finalLensName, content, model, temperature, tokens, interactionId };

  if (isCurrentConfigUnchanged(history, newConfig)) {
    return { added: false, currentIndex: history.currentIndex };
  }

  const version = getNextVersionNumber(finalLensName);

  const newItem: LensHistoryItem = {
    id: generateLensId(),
    name: finalLensName,
    content,
    model,
    temperature,
    tokens,
    createdAt: new Date().toISOString(),
    lastResult,
    isNamed: Boolean(lensName),
    version,
    interactionId,
    tenantId,
    tenantName
  };

  const newItems = trimHistory([...history.items, newItem]);

  const newHistory: LensHistory = {
    items: newItems,
    currentIndex: lastIndexOf(newItems)
  };

  saveLensHistory(newHistory);

  return { added: true, currentIndex: newHistory.currentIndex };
};

export const getCurrentLens = (): LensHistoryItem | null => {
  const history = getLensHistory();
  if (isValidIndex(history.currentIndex, history.items)) {
    return history.items[history.currentIndex];
  }
  return null;
};

const computeNewIndex = (
  direction: LensNavigationDirection,
  currentIndex: number,
  lastItemIndex: number
): number | null => {
  if (direction === LENS_NAVIGATION_DIRECTION.Previous && !isFirstItemIndex(currentIndex)) {
    return R.dec(currentIndex);
  }
  if (direction === LENS_NAVIGATION_DIRECTION.Next && currentIndex < lastItemIndex) {
    return R.inc(currentIndex);
  }
  return null;
};

export const navigateToLens = (direction: LensNavigationDirection): LensHistoryItem | null => {
  const history = getLensHistory();

  if (R.isEmpty(history.items)) {
    return null;
  }

  const newIndex = computeNewIndex(direction, history.currentIndex, lastIndexOf(history.items));

  if (newIndex === null) {
    return getCurrentLens();
  }

  const newHistory: LensHistory = {
    ...history,
    currentIndex: newIndex
  };

  saveLensHistory(newHistory);

  return history.items[newIndex];
};

export const getLensNavigationInfo = (): LensNavigationInfo => {
  const history = getLensHistory();

  return {
    canGoBack: !isFirstItemIndex(history.currentIndex),
    canGoForward: history.currentIndex < lastIndexOf(history.items),
    currentIndex: history.currentIndex,
    totalCount: history.items.length
  };
};

export const getLensHistoryForInteraction = (interactionId: string): LensHistoryItem[] => {
  const history = getLensHistory();
  return history.items.filter(item => item.interactionId === interactionId);
};

export const getAllLensHistory = (): LensHistoryItem[] => {
  const history = getLensHistory();
  return [...history.items];
};

export const clearLensHistory = (): void => {
  try {
    localStorage.removeItem(LENS_HISTORY_KEY);
  } catch (error) {
    logError("Failed to clear lens history from localStorage", error);
  }
};
